import React, { useState, useContext, FormEvent } from "react";
import { StylingContext } from "../App";

interface ImageWithContextMenuProps {
	imageUrl: string;
	label?: string;
	onDelete: () => void;
	onLabelChange?: (newLabel: string) => void;
}

const ImageWithContextMenu: React.FC<ImageWithContextMenuProps> = ({
	imageUrl,
	label = "",
	onDelete,
	onLabelChange
}) => {
	const [isHovered, setIsHovered] = useState(false);
	const [isEditing, setIsEditing] = useState(false);
	const [editValue, setEditValue] = useState(label);
	const { style } = useContext(StylingContext) || {};

	const handleMouseEnter = () => setIsHovered(true);
	const handleMouseLeave = () => setIsHovered(false);

	const handleDoubleClick = (e: React.MouseEvent) => {
		e.stopPropagation();
		setEditValue(label);
		setIsEditing(true);
	};

	const handleModalClose = () => {
		setIsEditing(false);
		setEditValue(label);
	};

	const handleModalSubmit = (e: FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		setIsEditing(false);
		if (onLabelChange && editValue !== label) {
			onLabelChange(editValue);
		}
	};

	const handleModalBgClick = (e: React.MouseEvent<HTMLDivElement>) => {
		const target = e.target as HTMLElement;
		if (target.id === "label-modal-bg") handleModalClose();
	};

	return (
		<>
			<div
				className="flex flex-col items-center"
				onMouseEnter={handleMouseEnter}
				onMouseLeave={handleMouseLeave}
				onDoubleClick={handleDoubleClick}
			>
				<div className="relative">
					<img src={imageUrl} className={style.ratio} style={{height: `${style.size}px`, width: `${style.size}px`}} alt="" />
					{isHovered && (
						<div
							className="absolute top-0 right-0 p-1 cursor-pointer text-red-500"
							onClick={onDelete}
							onDoubleClick={(e) => e.stopPropagation()}
						>
							<svg
								xmlns="http://www.w3.org/2000/svg"
								viewBox="0 0 24 24"
								fill="currentColor"
								className="w-4 h-4"
							>
								<path
									fillRule="evenodd"
									d="M12 2.25c-5.385 0-9.75 4.365-9.75 9.75s4.365 9.75 9.75 9.75 9.75-4.365 9.75-9.75S17.385 2.25 12 2.25zm-1.72 6.97a.75.75 0 10-1.06 1.06L10.94 12l-1.72 1.72a.75.75 0 101.06 1.06L12 13.06l1.72 1.72a.75.75 0 101.06-1.06L13.06 12l1.72-1.72a.75.75 0 10-1.06-1.06L12 10.94l-1.72-1.72z"
									clipRule="evenodd"
								/>
							</svg>
						</div>
					)}
				</div>
				{label && (
					<span
						className="mt-1 text-sm text-center text-gray-200 px-1 select-none"
						style={{ width: `${style.size}px`, maxWidth: `${style.size}px`, display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
						title={label}
					>
						{label}
					</span>
				)}
			</div>

			{isEditing && (
				<div
					className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50 dark:bg-opacity-70"
					id="label-modal-bg"
					onMouseDown={handleModalBgClick}
				>
					<div className="bg-zinc-800 p-6 rounded-md shadow-md">
						<h2 className="text-lg font-semibold mb-4 text-white">Edit Label</h2>
						<form onSubmit={handleModalSubmit}>
							<div className="mb-4">
								<label htmlFor="image-label" className="block text-sm font-medium text-gray-300">
									Label
								</label>
								<input
									autoFocus
									type="text"
									id="image-label"
									className="mt-1 p-2 w-full border rounded-md focus:ring focus:ring-indigo-300 text-black"
									value={editValue}
									onChange={(e) => setEditValue(e.target.value)}
									onMouseDown={(e) => e.stopPropagation()}
								/>
							</div>
							<div className="flex justify-end">
								<button
									type="button"
									className="mr-2 px-4 py-2 text-gray-400 hover:text-gray-100"
									onClick={handleModalClose}
								>
									Cancel
								</button>
								<button
									type="submit"
									className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
								>
									Save
								</button>
							</div>
						</form>
					</div>
				</div>
			)}
		</>
	);
};

export default ImageWithContextMenu;
